import { useEffect, useState } from 'react';
import { api } from './client';

/**
 * What the platform will accept.
 *
 * Read from the server rather than written into the screens, because several
 * of these are configurable per deployment: a customer running with a 500 MB
 * upload ceiling should be told 500 MB, not whatever the default happened to
 * be when the page was written.
 */
export interface PlatformLimits {
  upload: { maxFileMb: number; maxVideoMb: number; extensions: string[] };
  text: { maxTitleChars: number; maxBodyChars: number };
  invitations: { maxPerRequest: number; maxEmailChars: number };
  csvImport: {
    maxRows: number;
    maxFileMb: number;
    requiredColumns: string[];
    optionalColumns: string[];
  };
}

/**
 * Fetched once for the life of the tab.
 *
 * The values change only when the server is reconfigured and restarted, so
 * re-fetching them per screen would be a request per navigation for an answer
 * that cannot have moved.
 */
let cached: PlatformLimits | null = null;
let inFlight: Promise<PlatformLimits> | null = null;

export function useLimits(): PlatformLimits | null {
  const [limits, setLimits] = useState<PlatformLimits | null>(cached);

  useEffect(() => {
    if (cached) return;
    let live = true;

    inFlight ??= api<PlatformLimits>('/limits').then((result) => {
      cached = result;
      return result;
    });

    inFlight
      .then((result) => {
        if (live) setLimits(result);
      })
      .catch(() => {
        // A screen that cannot read the limits still works; it just says less.
        inFlight = null;
      });

    return () => {
      live = false;
    };
  }, []);

  return limits;
}

/** "5 GB" rather than "5120 MB", which nobody reads as five gigabytes. */
export function megabytes(value: number): string {
  if (value >= 1024 && value % 1024 === 0) return `${value / 1024} GB`;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} GB`;
  return `${value} MB`;
}
